import React, { useState } from 'react';

import { Palette, Type, Maximize, Layout, Check, AlertCircle, Info, AlertTriangle, Copy, CheckCircle } from 'lucide-react';
import Navigation from '../../components/Navigation';

const DesignSystemDocumentation = () => {
  const [copiedColor, setCopiedColor] = useState(null);

  const copyToClipboard = (text, colorName) => {
    navigator.clipboard?.writeText(text);
    setCopiedColor(colorName);
    setTimeout(() => setCopiedColor(null), 2000);
  };

  const colorPalette = {
    primary: [
      { name: 'Teal 700', hex: '#0F766E', rgb: 'rgb(15, 118, 110)', usage: 'Primary brand color, buttons, links' },
      { name: 'Teal 800', hex: '#115E59', rgb: 'rgb(17, 94, 89)', usage: 'Hover states, darker accents' },
      { name: 'Teal 900', hex: '#134E4A', rgb: 'rgb(19, 78, 74)', usage: 'Dark backgrounds, footer' }
    ],
    secondary: [
      { name: 'Green 600', hex: '#059669', rgb: 'rgb(5, 150, 105)', usage: 'Success states, positive actions' },
      { name: 'Green 700', hex: '#047857', rgb: 'rgb(4, 120, 87)', usage: 'Success hover states' }
    ],
    neutral: [
      { name: 'Gray 50', hex: '#F9FAFB', rgb: 'rgb(249, 250, 251)', usage: 'Page background' },
      { name: 'Gray 100', hex: '#F3F4F6', rgb: 'rgb(243, 244, 246)', usage: 'Card backgrounds' },
      { name: 'Gray 400', hex: '#9CA3AF', rgb: 'rgb(156, 163, 175)', usage: 'Muted text, placeholders' },
      { name: 'Gray 600', hex: '#4B5563', rgb: 'rgb(75, 85, 99)', usage: 'Body text' },
      { name: 'Gray 900', hex: '#111827', rgb: 'rgb(17, 24, 39)', usage: 'Headings, dark text' }
    ],
    semantic: [
      { name: 'Success', hex: '#10B981', rgb: 'rgb(16, 185, 129)', usage: 'Success messages, positive feedback' },
      { name: 'Warning', hex: '#F59E0B', rgb: 'rgb(245, 158, 11)', usage: 'Warning messages, caution states' },
      { name: 'Error', hex: '#EF4444', rgb: 'rgb(239, 68, 68)', usage: 'Error messages, destructive actions' },
      { name: 'Info', hex: '#3B82F6', rgb: 'rgb(59, 130, 246)', usage: 'Info messages, neutral notifications' }
    ]
  };

  const typography = {
    headings: [
      { name: 'H1', class: 'text-4xl font-bold', size: '36px / 2.25rem', weight: '700', example: 'Main Page Heading' },
      { name: 'H2', class: 'text-3xl font-bold', size: '30px / 1.875rem', weight: '700', example: 'Section Heading' },
      { name: 'H3', class: 'text-2xl font-bold', size: '24px / 1.5rem', weight: '700', example: 'Subsection Heading' },
      { name: 'H4', class: 'text-xl font-semibold', size: '20px / 1.25rem', weight: '600', example: 'Card Heading' },
      { name: 'H5', class: 'text-lg font-semibold', size: '18px / 1.125rem', weight: '600', example: 'Small Heading' },
      { name: 'H6', class: 'text-base font-semibold', size: '16px / 1rem', weight: '600', example: 'Tiny Heading' }
    ],
    body: [
      { name: 'Large', class: 'text-xl', size: '20px / 1.25rem', weight: '400', example: 'Large body text for emphasis' },
      { name: 'Base', class: 'text-base', size: '16px / 1rem', weight: '400', example: 'Standard body text for content' },
      { name: 'Small', class: 'text-sm', size: '14px / 0.875rem', weight: '400', example: 'Small text for captions' },
      { name: 'Tiny', class: 'text-xs', size: '12px / 0.75rem', weight: '400', example: 'Tiny text for labels' }
    ]
  };

  const spacing = {
    base: '8px',
    scale: [
      { name: 'xs', value: '4px', class: 'p-1', usage: 'Tight spacing, small elements' },
      { name: 'sm', value: '8px', class: 'p-2', usage: 'Compact spacing' },
      { name: 'md', value: '16px', class: 'p-4', usage: 'Standard spacing' },
      { name: 'lg', value: '24px', class: 'p-6', usage: 'Generous spacing, cards' },
      { name: 'xl', value: '32px', class: 'p-8', usage: 'Large spacing, sections' },
      { name: '2xl', value: '48px', class: 'p-12', usage: 'Extra large spacing' },
      { name: '3xl', value: '64px', class: 'p-16', usage: 'Maximum spacing, hero sections' }
    ]
  };

  const components = {
    buttons: [
      { 
        name: 'Primary', 
        class: 'bg-teal-700 hover:bg-teal-800 text-white px-6 py-3 rounded-lg font-semibold transition-colors',
        example: 'Primary Action'
      },
      { 
        name: 'Secondary', 
        class: 'bg-white text-teal-700 border-2 border-teal-700 hover:bg-teal-50 px-6 py-3 rounded-lg font-semibold transition-colors',
        example: 'Secondary Action'
      },
      { 
        name: 'Outline', 
        class: 'bg-transparent text-gray-700 border border-gray-300 hover:bg-gray-50 px-6 py-3 rounded-lg font-semibold transition-colors',
        example: 'Outline Button'
      },
      { 
        name: 'Ghost', 
        class: 'bg-transparent text-teal-700 hover:bg-teal-50 px-6 py-3 rounded-lg font-semibold transition-colors',
        example: 'Ghost Button'
      }
    ],
    cards: [
      { 
        name: 'Standard Card', 
        class: 'bg-white p-6 rounded-xl shadow-md',
        description: 'Default card with shadow'
      },
      { 
        name: 'Hover Card', 
        class: 'bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-shadow',
        description: 'Card with hover effect'
      },
      { 
        name: 'Bordered Card', 
        class: 'bg-white p-6 rounded-xl border border-gray-200',
        description: 'Card with border instead of shadow'
      }
    ],
    inputs: [
      { 
        name: 'Text Input', 
        class: 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500',
        placeholder: 'Enter text...'
      },
      { 
        name: 'Select', 
        class: 'w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500',
        placeholder: 'Select option...'
      }
    ]
  };

  const breakpoints = [
    { name: 'Mobile', min: '320px', max: '767px', class: 'sm:', usage: 'Phones, small devices' },
    { name: 'Tablet', min: '768px', max: '1023px', class: 'md:', usage: 'Tablets, medium devices' },
    { name: 'Desktop', min: '1024px', max: '1279px', class: 'lg:', usage: 'Desktops, large screens' },
    { name: 'Wide', min: '1280px', max: '∞', class: 'xl:', usage: 'Wide screens, large desktops' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-teal-700 to-teal-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-2xl md:text-3xl font-bold mb-4">
              Design System Documentation
            </h1>
            <p className="text-xl text-teal-100">
              Centralized reference for ValueBook's visual identity and component standards
            </p>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex flex-wrap gap-6 justify-center text-sm">
            <a href="#colors" className="text-teal-700 hover:text-teal-900 font-semibold">Colors</a>
            <a href="#typography" className="text-teal-700 hover:text-teal-900 font-semibold">Typography</a>
            <a href="#spacing" className="text-teal-700 hover:text-teal-900 font-semibold">Spacing</a>
            <a href="#components" className="text-teal-700 hover:text-teal-900 font-semibold">Components</a>
            <a href="#layout" className="text-teal-700 hover:text-teal-900 font-semibold">Layout</a>
            <a href="#accessibility" className="text-teal-700 hover:text-teal-900 font-semibold">Accessibility</a>
          </nav>
        </div>
      </section>

      {/* Color Palette Section */}
      <section id="colors" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Palette className="text-teal-700" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Color Palette</h2>
          </div>
          
          {/* Primary Colors */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Primary Colors</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {colorPalette?.primary?.map((color, index) => (
                <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div 
                    className="h-32 w-full cursor-pointer relative group" 
                    style={{ backgroundColor: color?.hex }}
                    onClick={() => copyToClipboard(color?.hex, color?.name)}
                  >
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                      {copiedColor === color?.name ? (
                        <CheckCircle className="text-white" size={32} />
                      ) : (
                        <Copy className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{color?.name}</h4>
                    <p className="text-sm text-gray-600 mb-1">HEX: {color?.hex}</p>
                    <p className="text-sm text-gray-600 mb-3">RGB: {color?.rgb}</p>
                    <p className="text-xs text-gray-500">{color?.usage}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Secondary Colors */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Secondary Colors</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {colorPalette?.secondary?.map((color, index) => (
                <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div 
                    className="h-32 w-full cursor-pointer relative group" 
                    style={{ backgroundColor: color?.hex }}
                    onClick={() => copyToClipboard(color?.hex, color?.name)}
                  >
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                      {copiedColor === color?.name ? (
                        <CheckCircle className="text-white" size={32} />
                      ) : (
                        <Copy className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{color?.name}</h4>
                    <p className="text-sm text-gray-600 mb-1">HEX: {color?.hex}</p>
                    <p className="text-sm text-gray-600 mb-3">RGB: {color?.rgb}</p>
                    <p className="text-xs text-gray-500">{color?.usage}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Neutral Colors */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Neutral Colors</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {colorPalette?.neutral?.map((color, index) => (
                <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200">
                  <div 
                    className="h-32 w-full cursor-pointer relative group" 
                    style={{ backgroundColor: color?.hex }}
                    onClick={() => copyToClipboard(color?.hex, color?.name)}
                  >
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                      {copiedColor === color?.name ? (
                        <CheckCircle className="text-gray-900" size={32} />
                      ) : (
                        <Copy className="text-gray-900 opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{color?.name}</h4>
                    <p className="text-sm text-gray-600 mb-1">HEX: {color?.hex}</p>
                    <p className="text-sm text-gray-600 mb-3">RGB: {color?.rgb}</p>
                    <p className="text-xs text-gray-500">{color?.usage}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Semantic Colors */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Semantic Colors</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {colorPalette?.semantic?.map((color, index) => (
                <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                  <div 
                    className="h-32 w-full cursor-pointer relative group" 
                    style={{ backgroundColor: color?.hex }}
                    onClick={() => copyToClipboard(color?.hex, color?.name)}
                  >
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all flex items-center justify-center">
                      {copiedColor === color?.name ? (
                        <CheckCircle className="text-white" size={32} />
                      ) : (
                        <Copy className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
                      )}
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{color?.name}</h4>
                    <p className="text-sm text-gray-600 mb-1">HEX: {color?.hex}</p>
                    <p className="text-sm text-gray-600 mb-3">RGB: {color?.rgb}</p>
                    <p className="text-xs text-gray-500">{color?.usage}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Typography Section */}
      <section id="typography" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Type className="text-teal-700" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Typography</h2>
          </div>

          <div className="mb-8">
            <div className="bg-teal-50 border-l-4 border-teal-700 p-4 mb-8">
              <p className="text-gray-700">
                <strong>Font Family:</strong> Inter (system default) with fallbacks: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif
              </p>
            </div>
          </div>
          
          {/* Headings */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Headings</h3>
            <div className="space-y-6">
              {typography?.headings?.map((heading, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{heading?.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{heading?.class}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Size: {heading?.size}</p>
                      <p className="text-sm text-gray-600">Weight: {heading?.weight}</p>
                    </div>
                    <div>
                      <p className={heading?.class}>{heading?.example}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Body Text */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Body Text</h3>
            <div className="space-y-6">
              {typography?.body?.map((body, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-center">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">{body?.name}</p>
                      <p className="text-xs text-gray-500 font-mono">{body?.class}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600">Size: {body?.size}</p>
                      <p className="text-sm text-gray-600">Weight: {body?.weight}</p>
                    </div>
                    <div>
                      <p className={body?.class}>{body?.example}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Spacing Section */}
      <section id="spacing" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Maximize className="text-teal-700" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Spacing System</h2>
          </div>

          <div className="mb-8">
            <div className="bg-teal-50 border-l-4 border-teal-700 p-4">
              <p className="text-gray-700">
                <strong>Base Unit:</strong> {spacing?.base} - All spacing follows an 8px grid system for consistency
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {spacing?.scale?.map((space, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-md">
                <div className="mb-4">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{space?.name}</h4>
                  <p className="text-sm text-gray-600 mb-1">Value: {space?.value}</p>
                  <p className="text-xs text-gray-500 font-mono mb-3">{space?.class}</p>
                  <p className="text-sm text-gray-600">{space?.usage}</p>
                </div>
                <div className="bg-teal-700" style={{ height: space?.value, width: '100%' }}></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Components Section */}
      <section id="components" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-8">
            <Layout className="text-teal-700" size={32} />
            <h2 className="text-3xl font-bold text-gray-900">Component Library</h2>
          </div>
          
          {/* Buttons */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Buttons</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {components?.buttons?.map((button, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">{button?.name}</h4>
                  <p className="text-xs text-gray-500 font-mono mb-4 break-all">{button?.class}</p>
                  <button className={button?.class}>
                    {button?.example}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Cards */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Cards</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {components?.cards?.map((card, index) => (
                <div key={index}>
                  <h4 className="font-semibold text-gray-900 mb-3">{card?.name}</h4>
                  <p className="text-xs text-gray-500 font-mono mb-4 break-all">{card?.class}</p>
                  <div className={card?.class}>
                    <h5 className="text-lg font-semibold text-gray-900 mb-2">Card Title</h5>
                    <p className="text-gray-600">{card?.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Form Inputs */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Form Inputs</h3>
            <div className="space-y-6">
              {components?.inputs?.map((input, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-3">{input?.name}</h4>
                  <p className="text-xs text-gray-500 font-mono mb-4 break-all">{input?.class}</p>
                  <input 
                    type="text" 
                    className={input?.class}
                    placeholder={input?.placeholder}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Layout & Grid Section */}
      <section id="layout" className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Layout & Grid System</h2>
          
          {/* Breakpoints */}
          <div className="mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Responsive Breakpoints</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {breakpoints?.map((breakpoint, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-md">
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">{breakpoint?.name}</h4>
                  <p className="text-sm text-gray-600 mb-2">Min: {breakpoint?.min} | Max: {breakpoint?.max}</p>
                  <p className="text-xs text-gray-500 font-mono mb-3">{breakpoint?.class}</p>
                  <p className="text-sm text-gray-600">{breakpoint?.usage}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Container Widths */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Container Widths</h3>
            <div className="space-y-4">
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="font-semibold text-gray-900 mb-2">max-w-7xl</h4>
                <p className="text-sm text-gray-600 mb-2">Maximum width: 1280px (80rem)</p>
                <p className="text-sm text-gray-600">Usage: Standard page container for most content sections</p>
              </div>
              <div className="bg-white p-6 rounded-xl shadow-md">
                <h4 className="font-semibold text-gray-900 mb-2">max-w-4xl</h4>
                <p className="text-sm text-gray-600 mb-2">Maximum width: 896px (56rem)</p>
                <p className="text-sm text-gray-600">Usage: Narrow content, CTA sections, forms</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Accessibility Section */}
      <section id="accessibility" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">Accessibility Guidelines</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-green-600">
              <div className="flex items-start gap-3">
                <Check className="text-green-600 flex-shrink-0" size={24} />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">WCAG 2.1 AA Compliance</h4>
                  <p className="text-sm text-gray-600">All color combinations meet minimum contrast ratios of 4.5:1 for normal text and 3:1 for large text</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-teal-700">
              <div className="flex items-start gap-3">
                <Info className="text-teal-700 flex-shrink-0" size={24} />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Focus Indicators</h4>
                  <p className="text-sm text-gray-600">All interactive elements have visible focus states using ring-2 ring-teal-500 for keyboard navigation</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-600">
              <div className="flex items-start gap-3">
                <AlertCircle className="text-blue-600 flex-shrink-0" size={24} />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Semantic HTML</h4>
                  <p className="text-sm text-gray-600">Use proper heading hierarchy (H1-H6), semantic elements (nav, main, footer), and ARIA labels where appropriate</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-yellow-600">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-yellow-600 flex-shrink-0" size={24} />
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Color is Not Alone</h4>
                  <p className="text-sm text-gray-600">Never use color alone to convey information - always pair with icons, text, or patterns</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-xl font-bold mb-4">ValueBook Design System</h3>
            <p className="text-gray-400 mb-6">
              Maintained by the ValueBook development team
            </p>
            <p className="text-sm text-gray-500">
              Last updated: December 2025 | Version 1.0
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DesignSystemDocumentation;